import React, { useState, useMemo, useContext } from "react";
import { Card, Button, Box } from "rebass";
import axios from "axios";
import { Form, Formik } from "formik";

import { AuthContext, MessageContext, MessageType } from "../../../../Contexts";
import {
  CardHeader,
  CardFooter,
  Table,
  Modal,
  DynamicInputFormType,
  DynamicInput,
} from "../../../../Components";
import { Patient, Measurement } from "../../../../Types";

export const MeasurementCard: React.FC<{
  patient: Patient;
  measurements: Measurement[];
  title: string;
  fields: DynamicInputFormType[];
  type: string;
  onMeasurementAdd: () => void;
}> = ({ patient, measurements, fields, title, type, onMeasurementAdd }) => {
  const authContext = useContext(AuthContext);
  const messageContext = useContext(MessageContext);

  const data = useMemo(() => measurements.filter((m) => m.type === type), [
    type,
    measurements,
  ]);

  const [openModal, setOpenModal] = useState(false);

  const defaultValues = useMemo(
    () =>
      fields.reduce((accum, value) => {
        return { ...accum, [value.accessor]: "" };
      }, {}),
    [fields]
  );

  const [initialValues, setInitialValues] = useState<{ [key: string]: any }>(
    defaultValues
  );

  const [editMeasurementId, setEditMeasurementId] = useState<null | string>(
    null
  );

  const [loading, setLoading] = useState(false);

  const saveMeasurement = async (values) => {
    if (!patient?.clinic_user_id) {
      return;
    }
    setLoading(true);
    try {
      await axios.post("/vue_api/add_measurement/", {
        clinic_user_id: patient?.clinic_user_id,
        measurements_json: JSON.stringify(values),
        type: type,
        p: authContext.user?.pass_hash,
        u: authContext.user?.user_name,
      });
      messageContext.addMessage("Measurement saved", MessageType.success);
      setLoading(false);
    } catch (e) {
      messageContext.addMessage("Something went wrong", MessageType.danger);
      console.log(e);
      setLoading(false);
    }
  };

  const deleteMeasurement = async (measurement_id: string) => {
    if (!patient?.clinic_user_id) {
      return;
    }
    setLoading(true);
    try {
      await axios.post("/vue_api/delete_measurement/", {
        clinic_user_id: patient?.clinic_user_id,
        measurement_id: measurement_id,
        type: type,
        p: authContext.user?.pass_hash,
        u: authContext.user?.user_name,
      });
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  };

  const editMeasurement = async (values) => {
    if (!editMeasurementId) {
      return;
    }
    await saveMeasurement(values);
    await deleteMeasurement(editMeasurementId);
  };

  const columns: any[] = useMemo(() => {
    const c: any[] = fields.map((field) => ({
      Header: field.Header,
      accessor: "measurements_json." + field.accessor,
    }));

    c.push({
      Header: "Edit",
      Cell: ({ row }) => (
        <Button
          onClick={() => {
            setEditMeasurementId(row.original.measurement_id);
            setInitialValues(row.original.measurements_json);
            setOpenModal(true);
          }}
          variant="textButton"
        >
          Edit
        </Button>
      ),
    });
    return c;
  }, [fields]);

  const onSubmit = async (values) => {
    if (editMeasurementId) {
      await editMeasurement(values);
    } else {
      await saveMeasurement(values);
    }

    setOpenModal(false);
    onMeasurementAdd();
  };

  data.sort((a, b) => {
    if (a.measurements_json && b.measurements_json) {
      if (a.measurements_json.Date < b.measurements_json.Date) {
        return 1;
      }
      if (a.measurements_json.Date > b.measurements_json.Date) {
        return -1;
      }
    }

    return 0;
  });

  return (
    <Card>
      <Modal
        isOpen={openModal}
        setIsOpen={setOpenModal}
        headerText={"Add " + title}
      >
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
          <Form>
            <Box
              padding={3}
              sx={{
                display: "grid",
                gridTemplateColumns: ["1fr", "1fr 1fr", "1fr 1fr 1fr"],
                columnGap: 3,
                rowGap: 3,
                justifyItems: "center",
              }}
            >
              {fields.map((field) => (
                <DynamicInput key={field.accessor} formType={field} />
              ))}
            </Box>
            <CardFooter>
              {editMeasurementId && (
                <Button
                  variant="danger"
                  marginRight={2}
                  disabled={loading}
                  onClick={async () => {
                    await deleteMeasurement(editMeasurementId);
                    setOpenModal(false);
                    onMeasurementAdd();
                  }}
                >
                  Delete
                </Button>
              )}
              <Button disabled={loading} type="submit">
                {editMeasurementId ? "Edit" : "Create"}
              </Button>
            </CardFooter>
          </Form>
        </Formik>
      </Modal>
      <CardHeader
        headerText={title}
        right={
          <Button
            onClick={() => {
              setInitialValues(defaultValues);
              setEditMeasurementId(null);
              setOpenModal(true);
            }}
          >
            Add Entry
          </Button>
        }
      />
      <Table columns={columns} data={data} />
    </Card>
  );
};
